import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Card, Button, Alert, ListGroup, Badge, Row, Col, Container, Spinner } from 'react-bootstrap'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../services/supabase'
import Layout from '../components/Layout'
import ProtectedRoute from '../components/ProtectedRoute'
import QRScanner from '../components/QRScanner'

function CheckinQR() {
  const { sessionId } = useParams()
  const navigate = useNavigate()
  const [session, setSession] = useState(null)
  const [presences, setPresences] = useState([])
  const [scanResult, setScanResult] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)

  // 🔒 Ref pour bloquer les scans simultanés (plus fiable que le state pour les callbacks)
  const scanningRef = useRef(false)
  // 🔒 Dernier token scanné pour ignorer les scans répétés du même QR
  const lastTokenRef = useRef('')
  const lastTokenTimerRef = useRef(null)

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      setUser(user)
    }
    getUser()
  }, [])

  const fetchSession = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select(`*, course_types (name, location)`)
        .eq('id', sessionId)
        .single()
      if (error) throw error
      setSession(data)
    } catch (error) {
      console.error('Error fetching session:', error)
      setError('Erreur lors du chargement de la session')
    }
  }, [sessionId])

  const fetchPresences = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('presences')
        .select(`*, participants (first_name, last_name, token)`)
        .eq('session_id', sessionId)
        .order('timestamp', { ascending: false })
      if (error) throw error
      setPresences(data || [])
    } catch (error) {
      console.error('Error fetching presences:', error)
    } finally {
      setLoading(false)
    }
  }, [sessionId])

  useEffect(() => {
    const loadData = async () => {
      await fetchSession()
      await fetchPresences()
    }
    loadData()
    // Nettoyage au démontage
    return () => {
      if (lastTokenTimerRef.current) clearTimeout(lastTokenTimerRef.current)
    }
  }, [fetchSession, fetchPresences])

  const handleScanSuccess = useCallback(async (decodedText) => {
    // Extraire le token de l'URL si nécessaire
    let token = decodedText.trim()
    try {
      const url = new URL(token)
      const urlToken = url.searchParams.get('token')
      if (urlToken) token = urlToken
    } catch {
      // Ce n'est pas une URL, on garde le token tel quel
    }

    // 🔒 1. Ignorer si un scan est déjà en cours
    if (scanningRef.current) return

    // 🔒 2. Ignorer si c'est le même token que le scan précédent (dans les 3 secondes)
    if (token === lastTokenRef.current) return

    scanningRef.current = true
    lastTokenRef.current = token

    // Réinitialiser le token "vu récemment" après 3 secondes
    if (lastTokenTimerRef.current) clearTimeout(lastTokenTimerRef.current)
    lastTokenTimerRef.current = setTimeout(() => {
      lastTokenRef.current = ''
    }, 3000)

    setScanResult(token)
    setError('')
    setSuccess('')

    try {
      // Chercher le participant par token
      const { data: participant, error: participantError } = await supabase
        .from('participants')
        .select('*')
        .eq('token', token)
        .single()

      if (participantError || !participant) {
        setError('QR Code invalide — Participant non trouvé')
        return
      }

      // ✅ upsert au lieu de insert — la contrainte UNIQUE en base garantit l'unicité
      const { error: presenceError } = await supabase
        .from('presences')
        .upsert(
          [{
            participant_id: participant.id,
            session_id: sessionId,
            mode: 'qr',
            timestamp: new Date().toISOString(),
          }],
          { onConflict: 'participant_id,session_id', ignoreDuplicates: true }
        )

      if (presenceError) {
        setError('Erreur lors du marquage : ' + presenceError.message)
        return
      }

      // Vérifier si c'était déjà présent (pour personnaliser le message)
      const wasAlreadyPresent = presences.some(p => p.participant_id === participant.id)
      if (wasAlreadyPresent) {
        setError(`${participant.first_name} ${participant.last_name} est déjà marqué présent`)
      } else {
        setSuccess(`✅ ${participant.first_name} ${participant.last_name} marqué présent !`)
        setTimeout(() => fetchPresences(), 400)
      }
    } catch (err) {
      console.error('Scan error:', err)
      setError('Erreur inattendue : ' + err.message)
    } finally {
      scanningRef.current = false
    }
  }, [sessionId, presences, fetchPresences])

  const handleScanError = (err) => {
    // Ignorer les erreurs de scan normales (caméra qui cherche)
  }

  if (loading) {
    return (
      <ProtectedRoute>
        <Layout user={user}>
          <Container className="text-center py-5" style={{ marginTop: '80px' }}>
            <Spinner animation="border" role="status" className="text-primary">
              <span className="visually-hidden">Chargement...</span>
            </Spinner>
            <p className="mt-3">Chargement de la session...</p>
          </Container>
        </Layout>
      </ProtectedRoute>
    )
  }

  if (!session) {
    return (
      <ProtectedRoute>
        <Layout user={user}>
          <Container style={{ marginTop: '80px' }}>
            <Alert variant="danger">
              <h4>Session non trouvée</h4>
              <p>La session demandée n'existe pas ou a été supprimée.</p>
              <Button variant="primary" onClick={() => navigate('/courses')}>Retour aux cours</Button>
            </Alert>
          </Container>
        </Layout>
      </ProtectedRoute>
    )
  }

  return (
    <ProtectedRoute>
      <Layout user={user}>
        <Container fluid="lg" style={{ marginTop: '80px', paddingBottom: '20px' }}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2>Présence par QR Code</h2>
              <p className="text-muted mb-0">
                <strong>Session :</strong> {session.course_types?.name || session.title}
                <span className="mx-2">•</span>
                <strong>Lieu :</strong> {session.course_types?.location || session.location}
                <span className="mx-2">•</span>
                <strong>Date :</strong> {new Date(session.date).toLocaleDateString('fr-FR')}
              </p>
            </div>
            <Button variant="outline-secondary" onClick={() => navigate('/courses')} size="lg">
              ← Retour aux Cours
            </Button>
          </div>

          <Row className="g-4">
            <Col md={6}>
              <Card className="shadow-sm h-100">
                <Card.Body className="d-flex flex-column">
                  <Card.Title className="text-center mb-4">📱 Scanner QR Code</Card.Title>

                  {error && (
                    <Alert variant="danger" className="mb-3" dismissible onClose={() => setError('')}>
                      ⚠️ {error}
                    </Alert>
                  )}

                  {success && (
                    <Alert variant="success" className="mb-3" dismissible onClose={() => setSuccess('')}>
                      {success}
                    </Alert>
                  )}

                  <div className="flex-grow-1">
                    <QRScanner
                      onScanSuccess={handleScanSuccess}
                      onScanError={handleScanError}
                    />
                  </div>

                  {scanResult && (
                    <div className="mt-3">
                      <Alert variant="info" className="mb-0">
                        <small><strong>Dernier scan :</strong> {scanResult.substring(0, 32)}…</small>
                      </Alert>
                    </div>
                  )}

                  <div className="mt-3 text-center">
                    <small className="text-muted">Positionnez le QR code dans le cadre pour scanner</small>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="shadow-sm h-100">
                <Card.Body className="d-flex flex-column">
                  <Card.Title className="text-center mb-3">
                    👥 Participants Présents
                    <Badge bg="primary" className="ms-2 fs-6">{presences.length}</Badge>
                  </Card.Title>

                  {presences.length === 0 ? (
                    <div className="text-center text-muted py-4 flex-grow-1 d-flex align-items-center justify-content-center">
                      <div>
                        <div style={{ fontSize: '3rem' }}>📭</div>
                        <p className="mt-2 mb-0">Aucun participant encore présent</p>
                      </div>
                    </div>
                  ) : (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }} className="flex-grow-1">
                      <ListGroup variant="flush">
                        {presences.map((presence) => (
                          <ListGroup.Item key={presence.id} className="px-0 py-2 border-bottom">
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="flex-grow-1">
                                <strong className="d-block">
                                  {presence.participants?.first_name} {presence.participants?.last_name}
                                </strong>
                                <small className="text-muted d-block">
                                  🕒 {new Date(presence.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                  <span className="mx-1">•</span>
                                  {presence.mode === 'qr' ? '📱 QR Code' : '👤 Manuel'}
                                </small>
                              </div>
                              <Badge bg="success">Présent</Badge>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-top">
                    <div className="row text-center text-muted small">
                      <div className="col">📱 QR : {presences.filter(p => p.mode === 'qr').length}</div>
                      <div className="col">👤 Manuel : {presences.filter(p => p.mode === 'name').length}</div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </Layout>
    </ProtectedRoute>
  )
}

export default CheckinQR
